import React, { useEffect, useState } from 'react';

import { Modal, Button, ListGroup, Spinner, Alert, Badge, Icon } from '../../../../core/ui';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { formatShortDate } from '../../../../core/format/date';

import {
  getTransactionSuggestions,
  linkReceiptToTransaction,
  unlinkReceiptFromTransaction,
} from '../../api/budget';
import { formatTry } from '../../format';
import styles from './ReceiptLinkModal.module.css';

/**
 * Binds a card transaction to the receipt of the same purchase, or removes
 * the binding. Candidates are the user's receipts dated around the charge,
 * best match first. onChanged receives the new link, or null after unlinking.
 */
const ReceiptLinkModal = ({ transaction, onClose, onChanged }) => {
  const { t, language } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = transaction.receiptLink;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const s = await getTransactionSuggestions(transaction.id);
        if (!cancelled) setSuggestions(s || []);
      } catch (err) {
        console.error('Failed to load receipt suggestions', err);
        if (!cancelled) setError(err?.message || t('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transaction.id, t]);

  const run = async (action) => {
    try {
      setBusy(true);
      setError(null);
      onChanged(await action());
      onClose();
    } catch (err) {
      console.error('Receipt link change failed', err);
      setError(err?.message || t('error'));
    } finally {
      setBusy(false);
    }
  };

  const link = (receiptId) => run(() => linkReceiptToTransaction(transaction.id, receiptId));
  const unlink = () => run(async () => {
    await unlinkReceiptFromTransaction(transaction.id, current.id);
    return null;
  });

  return (
    <Modal
      open={!!transaction}
      onClose={onClose}
      title={t('budget.linkReceipt')}
      footer={
        <>
          {current && (
            <Button variant="dangerGhost" onClick={unlink} disabled={busy}>
              <Icon name="fa-unlink" size="sm" />
              {t('budget.unlinkReceipt')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
        </>
      }
    >
      <div className={styles.txnInfo}>
        {formatShortDate(transaction.transactionDate, language)} · {transaction.merchantNormalized} ·{' '}
        {formatTry(transaction.amount)}
      </div>
      <p className={styles.hint}>{t('budget.linkReceiptHint')}</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <Spinner size="sm" label={t('loading')} />}

      {!loading && suggestions.length === 0 && (
        <div className={styles.emptyText}>{t('budget.noReceiptSuggestions')}</div>
      )}

      {!loading && suggestions.length > 0 && (
        <ListGroup className={styles.suggestionList}>
          {suggestions.map((s) => {
            const isCurrent = current?.receiptId === s.receiptId;
            return (
              <ListGroup.Item key={s.receiptId} className={styles.suggestionItem}>
                <div className={styles.suggestionText}>
                  <div className={styles.store}>{s.storeName || t('budget.unknownStore')}</div>
                  <div className={styles.meta}>
                    {formatShortDate(s.purchaseDateTime, language)} · {formatTry(s.totalAmount)}
                  </div>
                </div>
                <div className={styles.suggestionActions}>
                  <span className={styles.confidenceBadge} title={t('budget.matchScore')}>
                    {Math.round((s.confidence || 0) * 100)}%
                  </span>
                  {isCurrent ? (
                    <Badge variant="accent">{t('budget.receiptLinked')}</Badge>
                  ) : (
                    <Button size="sm" disabled={busy} onClick={() => link(s.receiptId)}>
                      {t('budget.link')}
                    </Button>
                  )}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}
    </Modal>
  );
};

export default ReceiptLinkModal;
